import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  getCoreRowModel,
  useReactTable,
  type PaginationState,
} from "@tanstack/react-table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTable } from "@/components/data-table/data-table";
import type { RiskFlag, RiskFlagPriority, RiskFlagStatus } from "@/lib/api/risk-flagging";
import { useRiskFlags } from "@/lib/api/risk-flagging";
import { createColumns } from "./columns";
import { flattenGroups, type RiskFlagRow } from "./types";
import { AlertDrawer } from "./alert-drawer";
import { CATEGORY_LABELS, PRIORITY_CONFIG } from "./constants";

const PRIORITY_OPTIONS: { value: RiskFlagPriority | "ALL"; label: string }[] = [
  { value: "ALL", label: "All tiers" },
  { value: "HIGH", label: "High risk" },
  { value: "MEDIUM", label: "Medium risk" },
  { value: "STANDARD", label: "Standard" },
];


const SELECT_CLASS =
  "rounded-[10px] border border-[#ece8e1] bg-white py-1.5 pl-3 pr-8 text-sm text-[#1f1e1d] focus:outline-none focus:ring-1 focus:ring-[#ece8e1]";

export function RiskFlaggingPage() {
  const [activeTab, setActiveTab] = useState<RiskFlagStatus>("OPEN");
  const [page, setPage] = useState(1);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedAlert, setSelectedAlert] = useState<RiskFlag | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [priorityFilter, setPriorityFilter] = useState<RiskFlagPriority | "ALL">("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data, isPending } = useRiskFlags(
    activeTab,
    page,
    pageSize,
    searchQuery,
    priorityFilter === "ALL" ? undefined : priorityFilter,
    categoryFilter === "ALL" ? undefined : categoryFilter,
    dateFrom ? dateFrom + "T00:00:00.000Z" : undefined,
    dateTo ? dateTo + "T23:59:59.999Z" : undefined,
  );

  const groups = data?.groups ?? [];
  const totalGroups = data?.totalGroups ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalGroups / pageSize));

  const rows = useMemo(
    () => flattenGroups(groups, expandedGroups),
    [groups, expandedGroups],
  );

  const pagination = useMemo<PaginationState>(
    () => ({ pageIndex: page - 1, pageSize }),
    [page, pageSize],
  );

  const cols = useMemo(() => createColumns(expandedGroups), [expandedGroups]);

  const table = useReactTable<RiskFlagRow>({
    data: rows,
    columns: cols,
    pageCount,
    state: { pagination },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(pagination) : updater;
      setPage(next.pageIndex + 1);
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize);
        setPage(1);
      }
    },
  });

  const resetPage = () => setPage(1);

  const handleRowClick = (row: RiskFlagRow) => {
    if (row._type === "group") {
      const hasMultiple = row.group.alertCount > 1;
      if (hasMultiple) {
        setExpandedGroups((prev) => {
          const next = new Set(prev);
          const key = row.group.groupKey;
          if (next.has(key)) next.delete(key);
          else next.add(key);
          return next;
        });
      } else {
        setSelectedAlert(row.group.latestAlert);
      }
    } else {
      setSelectedAlert(row.alert);
    }
  };

  const getRowClassName = (row: RiskFlagRow) => {
    if (row._type === "child") return "bg-zinc-100 hover:bg-zinc-200/70 text-xs";
    if (activeTab === "CLOSED") return "";
    const config = PRIORITY_CONFIG[row.group.priority];
    return [config?.rowBg, config?.rowBorder].filter(Boolean).join(" ");
  };

  return (
    <div className="px-[80px] py-[60px] font-body">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Risk flagging</h1>
        <p className="text-muted-foreground">
          Review moments of potential risk detected by the system
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v as RiskFlagStatus);
          setPage(1);
          setExpandedGroups(new Set());
        }}
        className="flex items-center"
      >
        <TabsList className="rounded-[10px] border border-zinc-200 bg-[#F5F4F2] p-1">
          <TabsTrigger
            value="OPEN"
            className="rounded-[8px] px-5 py-1.5 text-zinc-500 data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none"
          >
            Open
          </TabsTrigger>
          <TabsTrigger
            value="CLOSED"
            className="rounded-[8px] px-5 py-1.5 text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-zinc-900 data-[state=active]:shadow-none"
          >
            Closed
          </TabsTrigger>
        </TabsList>

        <div className="ml-auto flex items-center gap-3">
          <select
            value={priorityFilter}
            onChange={(e) => { setPriorityFilter(e.target.value as RiskFlagPriority | "ALL"); resetPage(); }}
            className={SELECT_CLASS}
          >
            {PRIORITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); resetPage(); }}
            className={SELECT_CLASS}
          >
            <option value="ALL">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => { setDateFrom(e.target.value); resetPage(); }}
            className={SELECT_CLASS}
          />
          <span className="text-sm text-[#a09a93]">—</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => { setDateTo(e.target.value); resetPage(); }}
            className={SELECT_CLASS}
          />

          <div className="relative w-[240px]">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#a09a93]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); resetPage(); }}
              placeholder="Search user, recipient, category..."
              className="w-full rounded-[10px] border border-[#ece8e1] bg-white py-1.5 pl-8 pr-3 text-sm text-[#1f1e1d] placeholder:text-[#a09a93] focus:outline-none focus:ring-1 focus:ring-[#ece8e1]"
            />
          </div>
        </div>
      </Tabs>

      <DataTable
        table={table}
        isPending={isPending}
        onRowClick={handleRowClick}
        getRowClassName={getRowClassName}
        getCanNextPage={() => page < pageCount}
        getCanPreviousPage={() => page > 1}
        onPageChange={(idx) => setPage(idx + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        className="mt-4"
      />

      {selectedAlert && (
        <AlertDrawer
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </div>
  );
}
